import { RouteType } from "types/RouteType";
import type { RootStore } from "@intrasync/stores";
import AccountsPayableStore from "./AccountsPayable.Store";

/**
 * Accounts Payable Route
 *
 * Main AP view per spec 07-accounts-payable.md
 * - Invoice queue (invoices pending payment)
 * - Payment batch creation
 * - NACHA file generation (AP-01)
 * - ACH tracking (AP-02)
 * - Check printing (AP-03)
 * - Quick payment (AP-05)
 */
const accountsPayableRoute: RouteType = {
  name: "Accounts Payable",
  path: "/accounting/accounts-payable",
  layout: "/app",
  viewportCard: {
    header: {
      title: "Accounts Payable",
      enableRefreshButton: true,
      enableBackButton: true,
      color: "primary",
      component: () => import("./AccountsPayable.Header"),
    },
    body: {
      component: () => import("./AccountsPayable.Body"),
      grayBackground: true,
    },
  },
  group: "Accounting",
  requiresAuth: true,
  requiresAdmin: false,
  requiresPermission: false,
  enableAutoFind: false,
  showCompanySelector: true,
  beforeEnter: async ({ rootStore }: { rootStore: RootStore }) => {
    const store = (rootStore as any).accountsPayableStore as AccountsPayableStore;
    if (store) {
      await store.loadData();
    }
    return true;
  },
  onParamsChange: async ({ rootStore }: { rootStore: RootStore }) => {
    const store = (rootStore as any).accountsPayableStore as AccountsPayableStore;
    if (store) {
      await store.loadData();
    }
  },
  dialogs: [
    {
      name: "paymentBatch",
      title: "Create Payment Batch",
      component: () => import("./dialogs/PaymentBatchDialog.Body"),
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
        const store = (rootStore as any).accountsPayableStore as AccountsPayableStore;
        await store.processPaymentBatch();
      },
      cancelButton: true,
      height: 600,
    },
    {
      name: "quickPayment",
      title: "Quick Payment",
      component: () => import("./dialogs/QuickPaymentDialog.Body"),
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
        const store = (rootStore as any).accountsPayableStore as AccountsPayableStore;
        await store.createQuickPayment();
      },
      cancelButton: true,
      height: 500,
    },
    {
      name: "nachaPreview",
      title: "NACHA File Preview",
      component: () => import("./dialogs/NACHAPreviewDialog.Body"),
      cancelButton: true,
      height: 550,
    },
    {
      name: "checkPrint",
      title: "Print Check",
      component: () => import("./dialogs/CheckPrintDialog.Body"),
      cancelButton: true,
      height: 450,
    },
    {
      name: "achForm",
      title: "Vendor ACH Information",
      component: () => import("./dialogs/ACHFormDialog.Body"),
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
        const store = (rootStore as any).accountsPayableStore as AccountsPayableStore;
        await store.saveVendorACHInfo(store.achFormData);
      },
      cancelButton: true,
      height: 400,
    },
    {
      name: "voidPayment",
      title: "Void Payment",
      component: () => import("./dialogs/VoidPaymentDialog.Body"),
      cancelButton: true,
      height: 300,
    },
    {
      name: "paymentDetail",
      title: "Payment Details",
      component: () => import("./dialogs/PaymentDetailDialog.Body"),
      cancelButton: true,
      height: 500,
    },
  ],
};

export default accountsPayableRoute;
